import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Button, DateRangePopover, FieldText, Modal } from "components/AtomsV2";

const CONFIRM_WORD = "APAGAR";

function DeleteRecordsModal({ open, onClose, onConfirm, isLoading, title }) {
  const { t } = useTranslation(["common"]);
  const [deleteMode, setDeleteMode] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [confirmText, setConfirmText] = useState("");

  useEffect(() => {
    if (open) {
      setDeleteMode("all");
      setStartDate("");
      setEndDate("");
      setConfirmText("");
    }
  }, [open]);

  const rangeInvalid = deleteMode === "between" && startDate && endDate && startDate > endDate;
  const betweenValid = deleteMode === "all" || (startDate && endDate && startDate <= endDate);
  const confirmInvalid = confirmText.length > 0 && confirmText !== CONFIRM_WORD;
  const canSubmit = confirmText === CONFIRM_WORD && betweenValid && !isLoading;

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm({
      startDate: deleteMode === "between" ? startDate : null,
      endDate: deleteMode === "between" ? endDate : null,
    });
  };

  const radio = (value, label) => (
    <label
      htmlFor={`delete-records-${value}`}
      className="flex items-center gap-[10px] cursor-pointer py-[5px]"
    >
      <input
        id={`delete-records-${value}`}
        type="radio"
        name="delete-records-mode"
        value={value}
        checked={deleteMode === value}
        onChange={(e) => setDeleteMode(e.target.value)}
        className="w-[18px] h-[18px] shrink-0"
        style={{ accentColor: "var(--accent)" }}
      />
      <span className="text-[14px] text-ink">{label}</span>
    </label>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      maxWidth={560}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("cancel", { defaultValue: "Cancelar" })}
          </Button>
          <button
            type="button"
            className="rec-btn-danger"
            disabled={!canSubmit}
            onClick={handleConfirm}
          >
            {isLoading
              ? t("deleting", { defaultValue: "A apagar…" })
              : t("delete", { defaultValue: "Apagar" })}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-[18px]">
        <div>
          <div className="text-[12.5px] font-semibold text-ink-2 mb-2">
            {t("delete_records_modal.what", { defaultValue: "O que pretende apagar?" })}
          </div>
          {radio("all", t("delete_records_modal.all", { defaultValue: "Todos os registos" }))}
          {radio(
            "between",
            t("delete_records_modal.between", { defaultValue: "Registos entre datas" })
          )}
        </div>

        {deleteMode === "between" && (
          <div className="rec-field">
            <span className="text-[12.5px] font-semibold text-ink-2">{t("datePicker.period")}</span>
            <div className="self-start">
              <DateRangePopover
                value={{ start: startDate, end: endDate }}
                onChange={({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                ariaLabel={t("datePicker.period")}
              />
            </div>
            {rangeInvalid && (
              <small>
                {t("delete_records_modal.invalid_range", { defaultValue: "Data inválida" })}
              </small>
            )}
          </div>
        )}

        <FieldText
          label={t("delete_records_modal.confirmation", { defaultValue: "Confirmação" })}
          placeholder={t("delete_records_modal.placeholder", { defaultValue: 'Escreva "APAGAR"' })}
          value={confirmText}
          onChange={setConfirmText}
          error={confirmInvalid}
          hint={
            confirmInvalid
              ? t("delete_records_modal.confirm_hint", {
                  defaultValue: 'Escreva "APAGAR" para habilitar a eliminação',
                })
              : undefined
          }
        />
      </div>
    </Modal>
  );
}

DeleteRecordsModal.defaultProps = {
  isLoading: false,
  title: "Apagar registos",
};

DeleteRecordsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
};

export default DeleteRecordsModal;
